import React, { useEffect, useState } from 'react';
import { contactApi } from '../services';
import companyUpdatesApi from '../services/companyUpdatesApi';
import enrichmentApi from '../services/enrichmentApi';
import RelationshipManagersField from './RelationshipManagersField';
import IcpField from './IcpField';
import FindingCard from './FindingCard';
import CompanyUpdates from './CompanyUpdates';
import Icon from './Icon';
import { getAdvisorColor, peopleLabel, targetAccountLabel, OUTREACH_STAGE_LABELS } from './utils';
import ProfilePdfBatchUpload from './ProfilePdfBatchUpload';
import ResearchButton from './ResearchButton';
import SiteAuditButton from './SiteAuditButton';
import { timeAgo } from '../utils/time';
import './CompanyDetailPanel.css';
import './AdminPanel.css';

const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const SUBJECT_POLL_MS = 3000;
const TABS = [
  ['overview', 'Overview'],
  ['pipeline', 'Pipeline'],
  ['documents', 'Documents'],
  ['updates', 'Updates'],
];

// enrichment.py's task kinds, keyed here so the "agent running" banner can name whatever's
// actually active - not just the two kinds this panel's own buttons start.
const ACTIVE_TASK_LABELS = {
  research: 'Researching…',
  site_audit: 'Auditing site… (usually 10–15 minutes)',
  icp_classification: 'Classifying ICP…',
};

/**
 * A company's details: research/audit actions, what's changed recently, then everything else in
 * tabs. Shared by the docked panel (CompanyDetailPanel) and the full-page view (CompanyPage) -
 * this owns all the data fetching; the two callers differ only in the chrome around it.
 */
const CompanyDetails = ({ companyId, name, onShowPeople, dataVersion = 0, onDataChanged, onHeaderInfo }) => {
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [findings, setFindings] = useState([]);
  const [findingsError, setFindingsError] = useState(null);
  const [findingsBusy, setFindingsBusy] = useState(false);
  const [recent, setRecent] = useState(null);
  const [tab, setTab] = useState('overview');
  const [pendingOpen, setPendingOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);

  useEffect(() => {
    setAnalytics(null);
    setError(null);
    setFindings([]);
    setFindingsError(null);
    setRecent(null);
    setTab('overview');
    setPendingOpen(false);
    setActiveTask(null);
  }, [companyId]);

  // Loads on open, and again quietly (keeping what is shown) after edits elsewhere bump dataVersion
  useEffect(() => {
    if (!companyId) return undefined;

    let cancelled = false;
    contactApi.getCompanyAnalytics(companyId)
      .then(data => {
        if (cancelled) return;
        setAnalytics(data);
        setError(null);
        onHeaderInfo?.({ name: data.company_name, industry: data.industry });
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataVersion]);

  // A research or site-audit run can finish long after anyone's watching the button that
  // started it — this is what makes its results visible when the panel is simply reopened.
  // Also polls while a run is active, from any origin (not just a button clicked in this
  // panel), so the "agent running" banner below reflects reality and clears on its own.
  useEffect(() => {
    if (!companyId) return undefined;

    let cancelled = false;
    let timer = null;

    const poll = () => {
      enrichmentApi.subject('company', companyId)
        .then(state => {
          if (cancelled) return;
          setFindings(state.findings.filter(finding => finding.status === 'proposed'));
          setActiveTask(state.active ? state.last_task : null);
          if (state.active) {
            timer = setTimeout(poll, SUBJECT_POLL_MS);
          }
        })
        .catch(() => {
          // Non-critical: the rest of the panel still works without this section
        });
    };
    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [companyId, dataVersion]);

  // The most recent activity, filtered to the last week - just the first page is plenty since
  // it's newest-first and this only ever shows up to 3.
  useEffect(() => {
    if (!companyId) return undefined;

    let cancelled = false;
    companyUpdatesApi.history(companyId)
      .then(page => {
        if (cancelled) return;
        const cutoff = Date.now() - RECENT_WINDOW_MS;
        setRecent((page.events || []).filter(event => new Date(event.occurred_at).getTime() >= cutoff).slice(0, 3));
      })
      .catch(() => {
        // Non-critical: the Updates tab still has the full history
        setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, dataVersion]);

  const decide = async (finding, decision) => {
    setFindingsBusy(true);
    try {
      await enrichmentApi[decision](finding.id);
      setFindings(current => current.filter(item => item.id !== finding.id));
      setFindingsError(null);
      onDataChanged?.();
    } catch (err) {
      setFindingsError(err.message);
    } finally {
      setFindingsBusy(false);
    }
  };

  if (!companyId) {
    return <p className="placeholder-text">No details are available for this company.</p>;
  }
  if (error) {
    return <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>;
  }
  if (!analytics) {
    return <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>;
  }

  const displayName = analytics.company_name || name;
  const maxSeniority = Math.max(1, ...(analytics.seniority || []).map(level => level.count));

  return (
    <>
      <ResearchButton subjectType="company" subjectId={companyId} onFinished={onDataChanged} />
      <SiteAuditButton subjectType="company" subjectId={companyId} onFinished={onDataChanged} />

      {activeTask && (
        <p className="company-active-task" role="status">
          <Icon name="loader" spin /> {ACTIVE_TASK_LABELS[activeTask.kind] || 'Agent running…'}
        </p>
      )}

      {findings.length > 0 && (
        <div className="company-section company-accordion">
          <div
            className="company-accordion__header"
            role="button"
            tabIndex={0}
            aria-expanded={pendingOpen}
            aria-controls="company-pending-review"
            onClick={() => setPendingOpen(open => !open)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setPendingOpen(open => !open);
              }
            }}
          >
            <h3>
              Pending review ({findings.length})
              {!pendingOpen && <Icon name="activity" className="icon--pulse company-accordion__pulse" />}
            </h3>
            <Icon
              name="chevron-down"
              className={`company-accordion__chevron${pendingOpen ? ' company-accordion__chevron--open' : ''}`}
            />
          </div>
          {pendingOpen && (
            <div id="company-pending-review">
              {findingsError && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {findingsError}</p>}
              <ul className="adm-events">
                {findings.map(finding => (
                  <FindingCard key={finding.id} finding={finding} busy={findingsBusy} showSubject={false} onDecide={decide} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {recent && recent.length > 0 && (
        <div className="company-section company-recent">
          <h3>This week</h3>
          <ul className="company-recent__list">
            {recent.map(event => (
              <li key={event.id}>
                <span className="company-recent__summary">{event.summary}</span>
                <span className="company-recent__when">{timeAgo(event.occurred_at)}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="company-recent__link" onClick={() => setTab('updates')}>
            See all updates <Icon name="chevron-right" />
          </button>
        </div>
      )}

      <div className="adm-tabs" role="tablist" aria-label="Company details">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`company-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`company-panel-${id}`}
            className={`adm-tabs__tab${tab === id ? ' active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="adm-tab" role="tabpanel" id={`company-panel-${tab}`} aria-labelledby={`company-tab-${tab}`}>
        {tab === 'overview' && (
          <>
            <IcpField
              companyId={companyId}
              companyName={displayName}
              icpSegmentId={analytics.icp_segment_id}
              icpName={analytics.icp_name}
              icpSource={analytics.icp_source}
              onChange={({ icp_segment_id, icp_name, icp_source }) => {
                setAnalytics(current => current && { ...current, icp_segment_id, icp_name, icp_source });
                onDataChanged?.();
              }}
            />

            <RelationshipManagersField
              companyId={companyId}
              companyName={displayName}
              managers={analytics.relationship_managers || []}
              onChange={(managers) => {
                setAnalytics(current => current && { ...current, relationship_managers: managers });
                onDataChanged?.();
              }}
            />

            {(analytics.description || analytics.hq_location || analytics.domain) && (
              <div className="company-section">
                <h3>About</h3>
                {analytics.description && <p className="company-about"><span>{analytics.description}</span></p>}
                {analytics.hq_location && <p className="company-about"><Icon name="map-pin" /> <span>{analytics.hq_location}</span></p>}
                {analytics.domain && (
                  <p className="company-about">
                    <Icon name="globe" />{' '}
                    <span><a href={`https://${analytics.domain}`} target="_blank" rel="noopener noreferrer">{analytics.domain}</a></span>
                  </p>
                )}
              </div>
            )}

            {(analytics.offerings || analytics.reach_out_roles) && (
              <div className="company-section">
                <h3>Offerings</h3>
                {analytics.offerings && <p className="company-about"><Icon name="briefcase" /> <span>{analytics.offerings}</span></p>}
                {analytics.reach_out_roles && (
                  <p className="company-about"><Icon name="user-check" /> <span>{analytics.reach_out_roles}</span></p>
                )}
              </div>
            )}

            <div className="company-section">
              <h3>People</h3>
              <div className="employee-count">
                <span className="count-number">{analytics.people_count}</span>
                <span className="count-label">
                  {[
                    analytics.network_people ? `${analytics.network_people} in advisors' networks` : null,
                    analytics.target_contacts ? `${analytics.target_contacts} target contacts` : null,
                    analytics.pdf_people ? `${analytics.pdf_people} added from LinkedIn PDFs` : null,
                    analytics.shared_people ? `${analytics.shared_people} known by more than one advisor` : null
                  ].filter(Boolean).join(' · ') || 'Nobody on file yet'}
                </span>
              </div>
              {onShowPeople && analytics.people_count > 0 && (
                <button type="button" className="company-panel-action" onClick={() => onShowPeople()}>
                  <Icon name="users" /> See all people
                </button>
              )}
            </div>

            <div className="company-section">
              <h3>By seniority</h3>
              <div className="position-breakdown">
                {analytics.seniority.filter(level => level.count > 0).map(level => (
                  <div className="position-item" key={level.level}>
                    <span className="position-name"><span>{level.label}</span></span>
                    <span className="company-panel-bar" aria-hidden="true">
                      <span style={{ width: `${(level.count / maxSeniority) * 100}%` }} />
                    </span>
                    <span className="position-count">{level.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="company-section">
              <h3>Who knows them</h3>
              <div className="position-breakdown">
                {analytics.advisors.map(advisor => (
                  <div className="position-item" key={advisor.name}>
                    <span className="position-name">
                      <span className="company-panel-dot" style={{ backgroundColor: getAdvisorColor(advisor.name) }} aria-hidden="true" />
                      <span>{advisor.name}</span>
                    </span>
                    <span className="position-count">{peopleLabel(advisor.people)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="company-section">
              <h3>Common titles</h3>
              <div className="position-breakdown">
                {analytics.positions.slice(0, 8).map(position => (
                  <div className="position-item" key={position.name}>
                    <span className="position-name"><span>{position.name}</span></span>
                    <span className="position-count">{position.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === 'pipeline' && (
          <>
            {analytics.target_accounts?.length > 0 && (
              <div className="company-section">
                <h3>On target lists</h3>
                {analytics.target_accounts.map(account => (
                  <div className="company-target-account" key={account.id}>
                    <div className="company-target-account__icp">
                      <Icon name="target" /> {account.icp_rank ? `${account.icp_rank}. ` : ''}{account.icp}
                    </div>
                    {account.account_name !== displayName && (
                      <div className="company-target-account__meta">Listed as {account.account_name}</div>
                    )}
                    <div className="company-target-account__meta">{targetAccountLabel(account, { withManagers: false })}</div>
                    <div className="company-target-account__meta">
                      {OUTREACH_STAGE_LABELS[account.outreach_stage] || 'No outreach recorded'}
                    </div>
                    {account.lead_offer && <div><strong>Lead offer:</strong> {account.lead_offer}</div>}
                    {account.route && <div><strong>Route:</strong> {account.route}</div>}
                    {account.fit_notes && <p className="company-target-account__notes">{account.fit_notes}</p>}
                    {account.status && <p className="company-target-account__notes">{account.status}</p>}
                  </div>
                ))}
              </div>
            )}

            {analytics.target_people?.length > 0 && (
              <div className="company-section">
                <h3>People to reach</h3>
                <div className="position-breakdown">
                  {analytics.target_people.map(person => (
                    <div className="position-item company-target-person" key={person.contact_id}>
                      <span className="position-name">
                        <span>
                          <strong>{person.name}</strong>{person.title ? ` – ${person.title}` : ''}
                          <span className="company-target-person__meta">
                            {person.role === 'to' ? 'Main contact' : 'CC'} · {person.email || person.email_status || 'no email yet'}
                            {person.known_by.length > 0 && ` · known by ${person.known_by.join(', ')}`}
                            {person.now_at && (
                              <strong className="company-target-person__moved">
                                {' '}· now at {person.now_at}{person.now_as ? ` as ${person.now_as}` : ''}
                              </strong>
                            )}
                          </span>
                        </span>
                      </span>
                      {person.do_not_call && (
                        <span className="company-target-person__dnc" role="img" aria-label="Phone on a do-not-call list" title="Phone on a do-not-call list">
                          <Icon name="alert-triangle" />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!analytics.target_accounts?.length && !analytics.target_people?.length && (
              <p className="placeholder-text">Not on a target list yet.</p>
            )}
          </>
        )}

        {tab === 'documents' && (
          <ProfilePdfBatchUpload companyId={companyId} companyName={displayName} onDataChanged={onDataChanged} />
        )}

        {tab === 'updates' && (
          <CompanyUpdates companyId={companyId} onDataChanged={onDataChanged} />
        )}
      </div>
    </>
  );
};

export default CompanyDetails;
